// src/api/website-api/web-order.js
import axios from "axios";

const WEB_ORDER_API = `${process.env.REACT_APP_API_POS_WITH_WEBSITE}/api/oc/create-order/`;

// Required headers
const baseHeaders = {
  "Language-ID": 1,
  "Content-Type": "application/json",
};

/**
 * Build order payload from POS selected items
 * This is the "simple" POS structure that createWebOrder will expand
 */
export const buildOrderPayload = ({
  selectedItems,
  customerInfo,
  totalAmount,
  paymentType = "cash",
  invoiceNo = "",
  comment = "",
}) => {
  return {
    // Customer info - use provided or "Guest" as default
    firstname: customerInfo?.firstname?.trim() || "Guest",
    lastname: customerInfo?.lastname?.trim() || "Customer",
    email: customerInfo?.email?.trim() || "guest@pos.local",
    telephone: customerInfo?.telephone?.trim() || "00000000",

    // Address - use provided or defaults for POS
    company: customerInfo?.company || "",
    address_1: customerInfo?.address_1?.trim() || "POS Sale",
    address_2: customerInfo?.address_2 || "POS Branch",
    city: customerInfo?.city?.trim() || "Kuwait City",
    postcode: customerInfo?.postcode?.trim() || "00000",
    country_id: customerInfo?.country_id || 114, // Kuwait
    zone_id: customerInfo?.zone_id || 1788,
    area_id: customerInfo?.area_id || 153,

    // Products - format for OC API
    products: selectedItems.map((item) => {
      const price = Number(item.Price || item.price || 0);
      const qty = Number(item.quantity || 1);
      return {
        product_id: Number(item._id || item.product_id || item.productId),
        name: item.Product_Name || item.name || "Unnamed",
        model: item.model || "POS Item",
        quantity: qty,
        price: price.toFixed(4), // API expects string with 4 decimals
        total: (price * qty).toFixed(4),
        tax: item.tax || 0,
        reward: item.reward || 0,
        master_id: item.master_id || 0,
      };
    }),

    // Payment type for internal use
    payment_type: paymentType,

    // Invoice number (for online payments)
    invoice_no: paymentType === "online" ? invoiceNo : "",

    // Total
    total: totalAmount,

    // Notes
    comment,
  };
};

/**
 * Create a new order via OC API
 * @param {Object} orderData - Order details from buildOrderPayload or custom
 * @returns {Promise<Object>} - API response
 */
export const createWebOrder = async (orderData) => {
  // 🔹 store info (from payload or localStorage)
  const storeId = orderData.store_id ?? localStorage.getItem("storeId") ?? 0;

  const storeUrl =
    orderData.store_url ??
    process.env.REACT_APP_STORE_URL ??
    (typeof window !== "undefined" ? window.location.origin : "");

  // 🔹 basic customer + address from orderData with Guest defaults
  const {
    // Customer info - with Guest defaults
    firstname = "Guest",
    lastname = "Customer",
    email = "guest@pos.local",
    telephone = "00000000",
    // Address info
    company = "",
    address_1 = "POS Sale",
    address_2 = "POS Branch",
    city = "Kuwait City",
    postcode = "00000",
    country_id = 114,
    zone_id = 1788,
    area_id = 153,
    // Products
    products = [],
    // Payment info
    payment_type = "cash",
    invoice_no = "",
    // Totals
    total,
    // Optional
    comment = "",
    custom_field = "[]",
    // Optional overrides
    customer_id = 0,
    order_status_id = 1,
  } = orderData;

  const countryName = orderData.country_name || "Kuwait";
  const zoneName = orderData.zone_name || "Kuwait City";

  // ✅ Ensure these are NEVER blank - use Guest defaults
  const safeFirstname = firstname?.trim() || "Guest";
  const safeLastname = lastname?.trim() || "Customer";
  const safeEmail = email?.trim() || "guest@pos.local";
  const safeTelephone = telephone?.trim() || "00000000";
  const safeAddress1 = address_1?.trim() || "POS Sale";
  const safeAddress2 = address_2?.trim() || "POS Branch";
  const safeCity = city?.trim() || "Kuwait City";
  const safePostcode = postcode?.trim() || "00000";

  // 🔹 Payment method mapping - OC format with code and name
  const getPaymentMethod = (type) => {
    switch (type) {
      case "online":
        return {
          code: "pos.online",
          name: "POS Online Payment",
        };
      case "cash":
      default:
        return {
          code: "pos.cod",
          name: "POS Cash on Delivery",
        };
    }
  };

  const paymentMethod = getPaymentMethod(payment_type);

  // 🔹 Format products for OC API
  const formattedProducts = products.map((item) => {
    const price = Number(item.price || item.Price || 0);
    const qty = Number(item.quantity || 1);
    return {
      product_id: Number(item.product_id || item._id || item.productId),
      name: String(item.name || item.Product_Name || "Unnamed"),
      model: String(item.model || "POS Item"),
      quantity: qty,
      price: price.toFixed(4),
      total: (price * qty).toFixed(4),
      tax: Number(item.tax || 0),
      reward: Number(item.reward || 0),
      master_id: Number(item.master_id || 0),
    };
  });

  // 🔹 OpenCart required fields (constant or derived)
  const payload = {
    // Store
    store_id: storeId,
    store_url: storeUrl,

    // Customer - using safe defaults
    customer_id,
    firstname: safeFirstname,
    lastname: safeLastname,
    email: safeEmail,
    telephone: safeTelephone,

    // Generic address - using safe defaults
    company,
    address_1: safeAddress1,
    address_2: safeAddress2,
    city: safeCity,
    postcode: safePostcode,
    country_id,
    zone_id,
    area_id,

    // Payment address (mirror) - using safe defaults
    payment_firstname: safeFirstname,
    payment_lastname: safeLastname,
    payment_company: company,
    payment_address_1: safeAddress1,
    payment_address_2: safeAddress2,
    payment_city: safeCity,
    payment_postcode: safePostcode,
    payment_country: countryName,
    payment_country_id: country_id,
    payment_zone: zoneName,
    payment_zone_id: zone_id,

    // Shipping address (same as payment) - using safe defaults
    shipping_firstname: safeFirstname,
    shipping_lastname: safeLastname,
    shipping_company: company,
    shipping_address_1: safeAddress1,
    shipping_address_2: safeAddress2,
    shipping_city: safeCity,
    shipping_postcode: safePostcode,
    shipping_country: countryName,
    shipping_country_id: country_id,
    shipping_zone: zoneName,
    shipping_zone_id: zone_id,

    // Products - properly formatted
    products: formattedProducts,

    // Payment method - OC format with code and name
    payment_method: paymentMethod,

    // Totals
    total,

    // Order status
    order_status_id,

    // Additional
    comment,
    custom_field,
    default: 1,

    // Invoice number for online payments
    ...(payment_type === "online" && invoice_no ? { invoice_no } : {}),

    // Client info
    user_agent:
      orderData.user_agent ||
      (typeof navigator !== "undefined" ? navigator.userAgent : "Hamilton POS"),
    ip: orderData.ip || "0.0.0.0",
  };

  console.log("📤 OC create order payload:", payload);

  const res = await axios.post(WEB_ORDER_API, payload, {
    headers: baseHeaders,
  });

  return res.data;
};
